import { generateVideoThumbnails } from "@rajesh896/video-thumbnails-generator";
import { uploadFileToS3 } from "api/media-library/upload-file-to-s3";
import { PostMediaFile } from "shared/types/post";

export interface VideoUploadProgress {
  phase: "thumbnail" | "video" | "complete";
  progress: number;
}

export interface VideoUploadResult {
  videoUrl: string;
  thumbnailUrl: string;
}

/**
 * Upload edited video with thumbnail generation
 */
export async function uploadEditedVideo({
  videoBlob,
  previousFile,
  onProgress,
}: {
  videoBlob: Blob;
  previousFile: PostMediaFile;
  onProgress?: (progress: VideoUploadProgress) => void;
}): Promise<PostMediaFile> {
  try {
    onProgress?.({ phase: "thumbnail", progress: 0 });

    // Generate thumbnail from video blob
    let thumbnailUrl = previousFile.thumbnailUrl; // fallback to original

    try {
      // Convert blob to File for thumbnail generation
      const videoFile = new File(
        [videoBlob],
        previousFile.fileName || "video.mp4",
        {
          type: "video/mp4",
        }
      );

      // Generate thumbnail (first frame)
      const thumbnails = await generateVideoThumbnails(videoFile, 1, "jpg");

      if (thumbnails && thumbnails.length > 0) {
        // Convert thumbnail data URL to blob
        const response = await fetch(thumbnails[0]);
        const thumbnailBlob = await response.blob();

        onProgress?.({ phase: "thumbnail", progress: 50 });

        // Upload thumbnail to S3
        const uploadedThumbnailUrl = await uploadFileToS3({
          blob: thumbnailBlob,
          fileName: `${previousFile.fileName || "video"}-thumb-${Date.now()}.jpg`,
        });

        thumbnailUrl = uploadedThumbnailUrl;
      }
    } catch (thumbnailError) {
      console.warn(
        "Thumbnail generation failed, using original thumbnail:",
        thumbnailError
      );
      // Continue with video upload even if thumbnail generation fails
    }

    onProgress?.({ phase: "video", progress: 0 });

    // Upload video to S3
    const videoUrl = await uploadFileToS3(
      {
        blob: videoBlob,
        fileName: previousFile.fileName || "edited-video.mp4",
      },
      {
        onProgress: (progress) => {
          onProgress?.({ phase: "video", progress });
        },
      }
    );

    onProgress?.({ phase: "complete", progress: 100 });

    // Return updated PostMediaFile
    return {
      originalUrl: previousFile.originalUrl || previousFile.url,
      altText: previousFile.altText,
      fileName: previousFile.fileName || "edited-video.mp4",
      isVideo: true,
      thumbnailUrl,
      url: videoUrl,
      type: "video/mp4",
      asset_id: previousFile.asset_id,
      meta: {
        ...previousFile.meta,
        size: videoBlob.size / 1024,
        isVideo: true,
      },
    };
  } catch (error) {
    console.error("Video upload failed:", error);
    throw error;
  }
}
